import { Link, useRouterState } from '@tanstack/react-router';
import { BusFront, MapPin, Database } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

const links = [
  { to: '/', label: 'Bus times', icon: BusFront },
  { to: '/stops', label: 'Choose stop', icon: MapPin },
  { to: '/schedules', label: 'Schedule data', icon: Database },
] as const;

export function AppShell({
  children,
  title,
  description,
}: {
  children: ReactNode;
  title: string;
  description: string;
}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/80 bg-card/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-5 py-4 sm:px-8">
          <Link
            to="/"
            data-jr-key="bus-times-nav-link"
            className="font-heading text-lg font-semibold tracking-tight text-foreground"
          >
            Seattle Bus Times
          </Link>
          <nav aria-label="Primary navigation" className="flex items-center gap-1">
            {links.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                data-jr-key={`${label.toLowerCase().replaceAll(' ', '-')}-nav-link`}
                aria-current={pathname === to ? 'page' : undefined}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
                  pathname === to
                    ? 'bg-secondary text-secondary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                <Icon className="size-4" aria-hidden="true" />
                <span className="hidden sm:inline">{label}</span>
              </Link>
            ))}
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14">
        <div className="max-w-2xl">
          <h1 className="font-heading text-4xl font-semibold tracking-[-0.035em] sm:text-5xl">{title}</h1>
          <p className="mt-3 max-w-xl text-base leading-7 text-muted-foreground">{description}</p>
        </div>
        <div className="mt-10">{children}</div>
      </main>
    </div>
  );
}
